import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { from } from 'rxjs';
import {
  AuditDialog,
  CreateAIAssessmentDialog,
  CreateAppAssessmentDialog,
  CreateCovidAssessmentDialog,
  CreateRiskAssessmentDialogDto,
  CreateTreatmentDialog
} from '../models';
import {
  AuditDialogComponent,
  CreateAiAssessmentDialogComponent,
  CreateAppAssessmentDialogComponent,
  CreateCovidAssessmentDialogComponent,
  CreateRiskAssessmentDialogComponent,
  CreateSimplifiedAppAssessmentDialogComponent,
  CreateTreatmentDialogComponent, DuplicateTreatmentHolderToResponsibleDialogComponent
} from '../components';
import { SimplifiedAppAssessment } from '@pt/pt-models';
import { PenTestDialog } from '../models/pen-test-dialog';
import { PenTestDialogComponent } from '../components/dialogs/pen-test-dialog';
import {
  GetApplicationForDetailsResponse
} from 'it/primatoo/openapi/api/org/schemas/application/getapplicationfordetailsresponse/GetApplicationForDetailsResponse';
import {
  DuplicateTreatmentHolderToResponsibleDialog
} from '../models/duplicate-treatment-holder-to-responsible-dialog';

const md = '500px';

@Injectable({
  providedIn: 'root',
})
export class DocumentsDialogService {
  constructor(private modalService: NgbModal) {}

  runDuplicateTreatmentHolderToResponsibleDialog = (
    createTreatmentDialog: DuplicateTreatmentHolderToResponsibleDialog
  ) => {
    const modalRef = this.modalService.open(
      DuplicateTreatmentHolderToResponsibleDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );

    modalRef.componentInstance.docId = createTreatmentDialog.docId;
    modalRef.componentInstance.openOnSuccess = createTreatmentDialog.openOnSuccess;
    modalRef.componentInstance.selectedFolderId =
      createTreatmentDialog.selectedFolderId;
    modalRef.componentInstance.selectedLegalEntityId =
      createTreatmentDialog.selectedLegalEntityId;
    modalRef.componentInstance.duplicateTreatmentPayload =
      createTreatmentDialog.duplicateTreatmentPayload;
    modalRef.componentInstance.selectedTreatmentCategoryId =
      createTreatmentDialog.selectedTreatmentCategoryId;
    modalRef.componentInstance.selectedDepartmentId =
      createTreatmentDialog.selectedDepartmentId;
    return from(modalRef.result);
  };

  runCreateTreatmentDialog = (createTreatmentDialog: CreateTreatmentDialog) => {
    const modalRef = this.modalService.open(CreateTreatmentDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });

    modalRef.componentInstance.dialogMode = createTreatmentDialog.dialogMode;
    modalRef.componentInstance.treatmentType =
      createTreatmentDialog.treatmentType;
    modalRef.componentInstance.openOnSuccess =
      createTreatmentDialog.openOnSuccess;
    modalRef.componentInstance.selectedFolderId =
      createTreatmentDialog.selectedFolderId;
    modalRef.componentInstance.selectedLegalEntityId =
      createTreatmentDialog.selectedLegalEntityId;
    modalRef.componentInstance.duplicateTreatmentPayload =
      createTreatmentDialog.duplicateTreatmentPayload;
    modalRef.componentInstance.selectedTreatmentCategoryId =
      createTreatmentDialog.selectedTreatmentCategoryId;
    modalRef.componentInstance.selectedDepartmentId =
      createTreatmentDialog.selectedDepartmentId;
    return from(modalRef.result);
  };

  runCreateAppAssessmentDialog = (
    createAppAssessmentDialog: CreateAppAssessmentDialog
  ) => {
    const modalRef = this.modalService.open(
      CreateAppAssessmentDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );

    modalRef.componentInstance.dialogMode =
      createAppAssessmentDialog.dialogMode;
    modalRef.componentInstance.openOnSuccess =
      createAppAssessmentDialog.openOnSuccess;
    modalRef.componentInstance.selectedFolderId =
      createAppAssessmentDialog.selectedFolderId;
    modalRef.componentInstance.selectedApplicationId =
      createAppAssessmentDialog.selectedApplicationId;
    modalRef.componentInstance.duplicateAppAssessmentPayload =
      createAppAssessmentDialog.duplicateAppAssessmentPayload;
    return from(modalRef.result);
  };

  runCreateSimplifiedAppAssessmentDialog = (createAppAssessmentDialog?: {
    dialogMode: 'CREATE' | 'EDIT';
    application?: GetApplicationForDetailsResponse;
    simplifiedAppAssessment?: SimplifiedAppAssessment;
  }) => {
    const modalRef = this.modalService.open(
      CreateSimplifiedAppAssessmentDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );

    modalRef.componentInstance.dialogMode =
      createAppAssessmentDialog.dialogMode;
    modalRef.componentInstance.application =
      createAppAssessmentDialog.application;
    modalRef.componentInstance.simplifiedAppAssessment =
      createAppAssessmentDialog.simplifiedAppAssessment;
    return from(modalRef.result);
  };

  runCreateCovidAssessmentDialog = (
    createCovidAssessmentDialog: CreateCovidAssessmentDialog
  ) => {
    const modalRef = this.modalService.open(
      CreateCovidAssessmentDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: md,
      }
    );
    modalRef.componentInstance.dialogMode =
      createCovidAssessmentDialog.dialogMode;
    modalRef.componentInstance.openOnSuccess =
      createCovidAssessmentDialog.openOnSuccess;
    modalRef.componentInstance.selectedFolderId =
      createCovidAssessmentDialog.selectedFolderId;
    modalRef.componentInstance.duplicateCovidAssessmentPayload =
      createCovidAssessmentDialog.duplicateCovidAssessmentPayload;
    return from(modalRef.result);
  };

  runAuditDialog = (auditDialog: AuditDialog) => {
    const modalRef = this.modalService.open(AuditDialogComponent, {
      centered: true,
      backdrop: 'static',
      // size: xl,
      // size: 'xxl',
      // size: 'full'
      size: 'audit',
    });
    modalRef.componentInstance.auditDialog = auditDialog;
    return from(modalRef.result);
  };

  runPenTestDialog = (penTestDialog: PenTestDialog) => {
    const modalRef = this.modalService.open(PenTestDialogComponent, {
      centered: true,
      backdrop: 'static',
      // size: xl
      // size: 'audit',
      size: 'full',
      keyboard: false,
    });
    modalRef.componentInstance.penTestDialog = penTestDialog;
    return from(modalRef.result);
  };

  runCreateAiAssessmentDialog = (
    createAIAssessmentDialog: CreateAIAssessmentDialog
  ) => {
    const modalRef = this.modalService.open(CreateAiAssessmentDialogComponent, {
      centered: true,
      backdrop: 'static',
      size: md,
    });
    modalRef.componentInstance.dialogMode = createAIAssessmentDialog.dialogMode;
    modalRef.componentInstance.openOnSuccess =
      createAIAssessmentDialog.openOnSuccess;
    modalRef.componentInstance.selectedFolderId =
      createAIAssessmentDialog.selectedFolderId;
    modalRef.componentInstance.selectedApplicationId =
      createAIAssessmentDialog.selectedApplicationId;
    modalRef.componentInstance.duplicateAiAssessmentPayload =
      createAIAssessmentDialog.duplicateAiAssessmentPayload;
    return from(modalRef.result);
  };

  runCreateRiskAssessmentDialog = (
    createRiskAssessmentDialog: CreateRiskAssessmentDialogDto
  ) => {
    const modalRef = this.modalService.open(
      CreateRiskAssessmentDialogComponent,
      {
        centered: true,
        backdrop: 'static',
        size: 'md',
      }
    );
    modalRef.componentInstance.dialogMode =
      createRiskAssessmentDialog.dialogMode;
    modalRef.componentInstance.openOnSuccess =
      createRiskAssessmentDialog.openOnSuccess;
    modalRef.componentInstance.parentId =
      createRiskAssessmentDialog.selectedFolderId;
    modalRef.componentInstance.createRiskAssessmentPayload =
      createRiskAssessmentDialog.createRiskAssessmentPayload;
    modalRef.componentInstance.duplicateRiskAssessmentPayload =
      createRiskAssessmentDialog.duplicateRiskAssessmentPayload;
    return from(modalRef.result);
  };
}
